import React, { useRef, useState } from 'react';
import { consoleTokens, Kbd } from '@components/ui/console';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { PdfPrefs, XlsxPrefs } from '../hooks/useReportRun';
import type { ReportColumn, ReportCsvExportProfile, ReportDimension } from '../types';

const MAX_PREVIEW_TABS = 5;
const MAX_PREVIEW_COLUMNS = 10;

export interface ExportMenuProps {
  dimensions: ReportDimension[];
  /** The current view's columns, in order. */
  viewColumns: ReportColumn[];
  /** The full column catalog, in order. */
  allColumns: ReportColumn[];
  /** Band labels from the current run, keyed by its primary grouping. */
  currentGroupLabels: string[];
  /** The current primary group-by dimension key ('' when ungrouped). */
  currentPrimaryDimension: string;
  /** Stored Excel options (tabs default to the primary grouping). */
  xlsxPrefs: XlsxPrefs;
  /** Stored PDF options (pages default to the primary grouping). */
  pdfPrefs: PdfPrefs;
  /** Definition-owned CSV layouts for downstream imports. */
  csvExportProfiles?: ReportCsvExportProfile[];
  disabled?: boolean;
  onCsv: (options: { allColumns: boolean }) => void;
  onCsvProfile?: (profileKey: string) => void;
  onXlsx: (options: XlsxPrefs) => void;
  onPdf: (options: PdfPrefs) => void;
}

const TabChip: React.FC<{ label: string; active?: boolean }> = ({ label, active }) => (
  <Box
    sx={{
      bgcolor: active ? '#FFFFFF' : consoleTokens.tokenBg,
      border: `1px solid ${consoleTokens.line}`,
      borderBottom: active ? '1px solid #FFFFFF' : `1px solid ${consoleTokens.line}`,
      borderRadius: '4px 4px 0 0',
      color: active ? consoleTokens.ink : consoleTokens.dim,
      fontSize: '0.625rem',
      maxWidth: 110,
      overflow: 'hidden',
      px: 1,
      py: 0.25,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

/**
 * One Export button, three paths: CSV of the current view (the `E`
 * shortcut), CSV of everything, and an Excel workbook dialog with a live
 * preview of the tab/column layout. Workbook options persist per report.
 */
export const ExportMenu: React.FC<ExportMenuProps> = ({
  dimensions,
  viewColumns,
  allColumns: allColumnDefs,
  currentGroupLabels,
  currentPrimaryDimension,
  xlsxPrefs,
  pdfPrefs,
  csvExportProfiles = [],
  disabled,
  onCsv,
  onCsvProfile,
  onXlsx,
  onPdf,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetBy, setSheetBy] = useState(xlsxPrefs.sheetBy);
  const [allColumns, setAllColumns] = useState(xlsxPrefs.allColumns);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pageBy, setPageBy] = useState(pdfPrefs.pageBy);
  const [pdfAllColumns, setPdfAllColumns] = useState(pdfPrefs.allColumns);

  const openDialog = () => {
    setSheetBy(xlsxPrefs.sheetBy);
    setAllColumns(xlsxPrefs.allColumns);
    setMenuOpen(false);
    setDialogOpen(true);
  };

  const openPdfDialog = () => {
    setPageBy(pdfPrefs.pageBy);
    setPdfAllColumns(pdfPrefs.allColumns);
    setMenuOpen(false);
    setPdfDialogOpen(true);
  };

  // PDF preview — mirrors the renderer: orientation from estimated column
  // widths; the fill column (notes) becomes a sub-row, not a grid column.
  const PDF_TYPE_WIDTHS: Record<string, number> = {
    datetime: 80,
    date: 58,
    cents: 62,
    int: 40,
    decimal: 46,
    bool: 44,
    string: 92,
  };
  const pdfColumnSet = pdfAllColumns ? allColumnDefs : viewColumns;
  const pdfGridColumns = pdfColumnSet.filter(column => !column.fill && column.key !== pageBy);
  const pdfHasNotes = pdfColumnSet.some(column => column.fill);
  const pdfLandscape =
    pdfGridColumns.reduce((sum, column) => sum + (PDF_TYPE_WIDTHS[column.type] ?? 92), 0) > 540;
  const pageByDimension = dimensions.find(dimension => dimension.key === pageBy);
  const pdfPageCount =
    pageBy && pageBy === currentPrimaryDimension && currentGroupLabels.length > 0
      ? currentGroupLabels.length
      : pageBy
        ? null
        : 1;
  const pdfFirstPageLabel = pageBy
    ? ((pageBy === currentPrimaryDimension ? currentGroupLabels[0] : undefined) ??
      `(one per ${pageByDimension?.label.toLowerCase() ?? 'value'})`)
    : '';

  const sheetByDimension = dimensions.find(dimension => dimension.key === sheetBy);
  // Real tab names are only known client-side when tabs follow the current
  // grouping; otherwise the preview shows the shape, not the names.
  const previewTabs: string[] =
    sheetBy === ''
      ? ['Details']
      : sheetBy === currentPrimaryDimension && currentGroupLabels.length > 0
        ? currentGroupLabels.slice(0, MAX_PREVIEW_TABS)
        : [`(one per ${sheetByDimension?.label.toLowerCase() ?? 'value'})`];
  const hiddenTabCount =
    sheetBy === currentPrimaryDimension
      ? Math.max(currentGroupLabels.length - MAX_PREVIEW_TABS, 0)
      : 0;

  const viewColumnLabels = viewColumns.map(column => column.label);
  const allColumnLabels = allColumnDefs.map(column => column.label);
  const previewColumns = (allColumns ? allColumnLabels : viewColumnLabels).slice(
    0,
    MAX_PREVIEW_COLUMNS,
  );
  const hiddenColumnCount =
    (allColumns ? allColumnLabels : viewColumnLabels).length - previewColumns.length;

  const rowSx = { fontSize: '0.75rem', minHeight: 32 };
  const sectionLabelSx = {
    color: consoleTokens.fainter,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    mb: 0.5,
    textTransform: 'uppercase' as const,
  };

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
        onClick={() => setMenuOpen(true)}
        disabled={disabled}
        sx={{ color: consoleTokens.ink, fontSize: '0.75rem', textTransform: 'none' }}
      >
        Export
      </Button>

      <Menu open={menuOpen} anchorEl={anchorRef.current} onClose={() => setMenuOpen(false)}>
        <MenuItem
          sx={rowSx}
          onClick={() => {
            setMenuOpen(false);
            onCsv({ allColumns: false });
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
            <span>CSV · this view ({viewColumnLabels.length} columns)</span>
            <Box sx={{ flex: 1 }} />
            <Kbd>E</Kbd>
          </Stack>
        </MenuItem>
        <MenuItem
          sx={rowSx}
          onClick={() => {
            setMenuOpen(false);
            onCsv({ allColumns: true });
          }}
        >
          CSV · all columns ({allColumnLabels.length})
        </MenuItem>
        {csvExportProfiles.length > 0 && <Divider />}
        {csvExportProfiles.map(profile => (
          <MenuItem
            key={profile.key}
            sx={{
              ...rowSx,
              alignItems: 'flex-start',
              maxWidth: 360,
              py: 0.75,
              whiteSpace: 'normal',
            }}
            onClick={() => {
              setMenuOpen(false);
              onCsvProfile?.(profile.key);
            }}
          >
            <Stack spacing={0.15}>
              <span>{profile.label}</span>
              {profile.description && (
                <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem' }}>
                  {profile.description}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem sx={rowSx} onClick={openDialog}>
          Excel workbook…
        </MenuItem>
        <MenuItem sx={rowSx} onClick={openPdfDialog}>
          PDF document…
        </MenuItem>
      </Menu>

      {/* PDF document options: the printable day sheet. */}
      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ ...sectionLabelSx, mb: 2 }}>PDF document</Typography>

          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem', mb: 0.5 }}>
            Pages
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={pageBy}
            onChange={(_, next: string | null) => next !== null && setPageBy(next)}
            sx={{
              mb: 1.5,
              '& .MuiToggleButton-root': { fontSize: '0.6875rem', py: 0.5, textTransform: 'none' },
            }}
          >
            <ToggleButton value="">Continuous</ToggleButton>
            {dimensions.map(dimension => (
              <ToggleButton key={dimension.key} value={dimension.key}>
                One per {dimension.label.toLowerCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem', mb: 0.5 }}>
            Columns
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={pdfAllColumns ? 'all' : 'view'}
            onChange={(_, next: string | null) => next && setPdfAllColumns(next === 'all')}
            sx={{
              mb: 1.5,
              '& .MuiToggleButton-root': { fontSize: '0.6875rem', py: 0.5, textTransform: 'none' },
            }}
          >
            <ToggleButton value="view">This view ({viewColumnLabels.length})</ToggleButton>
            <ToggleButton value="all">All ({allColumnLabels.length})</ToggleButton>
          </ToggleButtonGroup>

          {/* Page thumbnail: branded header, section band, row scaffold with
              pink note sub-rows — the document's shape at a glance. */}
          <Typography sx={sectionLabelSx}>Preview</Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              {(pdfPageCount === null || pdfPageCount > 1) && (
                <Box
                  sx={{
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${consoleTokens.line}`,
                    borderRadius: '3px',
                    height: pdfLandscape ? 108 : 152,
                    left: 5,
                    position: 'absolute',
                    top: 5,
                    width: pdfLandscape ? 152 : 118,
                  }}
                />
              )}
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: `1px solid ${consoleTokens.line}`,
                  borderRadius: '3px',
                  boxShadow: '0 2px 8px rgba(38, 34, 46, 0.08)',
                  height: pdfLandscape ? 108 : 152,
                  p: 1,
                  position: 'relative',
                  width: pdfLandscape ? 152 : 118,
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'baseline',
                    borderBottom: '1.5px solid #1e2d6b',
                    justifyContent: 'space-between',
                    pb: 0.4,
                  }}
                >
                  <Typography sx={{ color: '#1e2d6b', fontSize: '0.5rem', fontWeight: 800 }}>
                    JotPsych
                  </Typography>
                  <Box sx={{ bgcolor: '#1e2d6b', borderRadius: '1px', height: 5, width: 28 }} />
                </Stack>
                {pageBy && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: '#1e2d6b',
                      borderRadius: '2px',
                      display: 'flex',
                      mt: 0.6,
                      px: 0.6,
                      py: 0.2,
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{ color: '#FFFFFF', fontSize: '0.4375rem', fontWeight: 700 }}
                    >
                      {pdfFirstPageLabel}
                    </Typography>
                  </Box>
                )}
                <Stack spacing={0.5} sx={{ mt: 0.7 }}>
                  {[0, 1, 2, 3].map(index => (
                    <React.Fragment key={index}>
                      <Stack direction="row" spacing={0.4}>
                        {pdfGridColumns.slice(0, pdfLandscape ? 7 : 5).map((column, c) => (
                          <Box
                            key={column.key}
                            sx={{
                              bgcolor: c === 0 ? '#c9d2f2' : consoleTokens.line,
                              borderRadius: '1px',
                              flex: c === 1 ? 2 : 1,
                              height: 4,
                            }}
                          />
                        ))}
                      </Stack>
                      {pdfHasNotes && index % 2 === 0 && (
                        <Box
                          sx={{
                            bgcolor: '#fdf7f9',
                            borderLeft: '2px solid #f4b8c8',
                            borderRadius: '1px',
                            height: 4,
                            ml: 0.5,
                            width: '55%',
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
                <Typography
                  sx={{
                    bottom: 3,
                    color: consoleTokens.fainter,
                    fontSize: '0.375rem',
                    left: 8,
                    position: 'absolute',
                  }}
                >
                  JotPsych · generated …
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ color: consoleTokens.fainter, fontSize: '0.6875rem' }}>
              {pdfPageCount === 1
                ? 'A single continuous document'
                : pdfPageCount === null
                  ? `One page per ${pageByDimension?.label.toLowerCase() ?? 'value'}`
                  : `${pdfPageCount} ${pdfPageCount === 1 ? 'page' : 'pages'} — one per ${
                      pageByDimension?.label.toLowerCase() ?? 'value'
                    }`}
              {' · '}letter {pdfLandscape ? 'landscape' : 'portrait'}.
              {pdfHasNotes ? ' Notes print on their own line under each row.' : ''} Rows print in
              the on-screen order.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={() => setPdfDialogOpen(false)}
            sx={{ color: consoleTokens.dim, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              setPdfDialogOpen(false);
              onPdf({ pageBy, allColumns: pdfAllColumns });
            }}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ ...sectionLabelSx, mb: 2 }}>Excel workbook</Typography>

          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem', mb: 0.5 }}>
            Sheets
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={sheetBy}
            onChange={(_, next: string | null) => next !== null && setSheetBy(next)}
            sx={{
              mb: 1.5,
              '& .MuiToggleButton-root': { fontSize: '0.6875rem', py: 0.5, textTransform: 'none' },
            }}
          >
            <ToggleButton value="">Single sheet</ToggleButton>
            {dimensions.map(dimension => (
              <ToggleButton key={dimension.key} value={dimension.key}>
                One per {dimension.label.toLowerCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem', mb: 0.5 }}>
            Columns
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={allColumns ? 'all' : 'view'}
            onChange={(_, next: string | null) => next && setAllColumns(next === 'all')}
            sx={{
              mb: 2.5,
              '& .MuiToggleButton-root': { fontSize: '0.6875rem', py: 0.5, textTransform: 'none' },
            }}
          >
            <ToggleButton value="view">This view ({viewColumnLabels.length})</ToggleButton>
            <ToggleButton value="all">All ({allColumnLabels.length})</ToggleButton>
          </ToggleButtonGroup>

          {/* Workbook preview: tab strip + first sheet's header row */}
          <Typography sx={sectionLabelSx}>Preview</Typography>
          <Box
            sx={{
              border: `1px solid ${consoleTokens.line}`,
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              spacing={0.25}
              sx={{
                alignItems: 'flex-end',
                bgcolor: consoleTokens.bandBg,
                borderBottom: `1px solid ${consoleTokens.line}`,
                pt: 1,
                px: 1,
              }}
            >
              <TabChip label="Summary" />
              {previewTabs.map((tab, index) => (
                <TabChip key={`${tab}-${index}`} label={tab} active={index === 0} />
              ))}
              {hiddenTabCount > 0 && <TabChip label={`+${hiddenTabCount} more`} />}
            </Stack>
            <Box sx={{ overflowX: 'hidden', p: 1 }}>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {previewColumns.map(label => (
                  <Box
                    key={label}
                    sx={{
                      bgcolor: consoleTokens.tokenBg,
                      border: `1px solid ${consoleTokens.line}`,
                      borderRadius: '4px',
                      color: consoleTokens.dim,
                      fontSize: '0.625rem',
                      px: 0.75,
                      py: 0.25,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </Box>
                ))}
                {hiddenColumnCount > 0 && (
                  <Typography
                    sx={{ alignSelf: 'center', color: consoleTokens.fainter, fontSize: '0.625rem' }}
                  >
                    +{hiddenColumnCount} more
                  </Typography>
                )}
              </Stack>
              {/* Faint data-row scaffold so it reads as a sheet, not a list */}
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {[0, 1, 2].map(index => (
                  <Box
                    key={index}
                    sx={{ bgcolor: consoleTokens.rowLine, borderRadius: '2px', height: 8 }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={() => setDialogOpen(false)}
            sx={{ color: consoleTokens.dim, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              setDialogOpen(false);
              onXlsx({ sheetBy, allColumns });
            }}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            Download workbook
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
